// src/utils/predicao.js
import { normalizarResultadoLab } from './resultadoLab';

/**
 * Leitura do `predicao` do experimento — o resultado do modelo que o crm-back grava
 * ao processar (POST /experimento/processar). Único lugar do front que conhece esse
 * formato; as telas perguntam por alvo e unidade.
 *
 * Formato (Experiments.Predicao no crm-back):
 *   { resultado: 'CONTAGEM' | 'AUSENCIA', modo, modelo, processadoEm, processadoPor,
 *     alvos: [{ alvo, presenca, contagens: [{ unidade, log10, valorAbsoluto }] }] }
 *
 * `log10` já vem SEM a diluição que o experimento tinha ao ser processado. Quem
 * exibe soma a diluição ATUAL (`comDiluicao`), então alterar a diluição depois de
 * processar vale sem reprocessar — exceto quando o próprio resultado diz que a
 * diluição não se aplica (`diluicaoAplicavel: false`, leite e colostro).
 *
 * Toda tela que mostra o resultado (card de Amostras, diálogo do Processar) monta as
 * linhas com `secoesDoResultado`, para todas mostrarem o mesmo número.
 */

/** Nomes dos alvos como o modelo os chama. */
export const ALVO = Object.freeze({
    COLIFORMES_TOTAIS: 'colitotais',
    ECOLI: 'ecoli',
    MESOFILOS_TOTAIS: 'mesofilostotais',
});

const alvoDa = (predicao, alvo) =>
    (predicao?.alvos || []).find((a) => a?.alvo === alvo);

/** Ausência detectada: o modelo não chegou a estimar contagem. */
export const isAusencia = (predicao) => predicao?.resultado === 'AUSENCIA';

/** O modelo só respondeu presença/ausência, sem contagem. */
export const isSoPresenca = (predicao) =>
    isAusencia(predicao) || predicao?.modo === 'presence_absence';

/**
 * Presença do alvo: `true`, `false` ou `undefined` quando o modelo não informou.
 * Na ausência detectada, todo alvo está ausente.
 */
export const presencaDoAlvo = (predicao, alvo) => {
    if (!predicao) return undefined;
    if (isAusencia(predicao)) return false;
    const presenca = alvoDa(predicao, alvo)?.presenca;
    return typeof presenca === 'boolean' ? presenca : undefined;
};

/** log10 da contagem do alvo na unidade (`NMP`/`UFC`), sem diluição; ou `null`. */
export const contagemLog10 = (predicao, alvo, unidade) => {
    const contagem = (alvoDa(predicao, alvo)?.contagens || []).find(
        (c) => c?.unidade === unidade
    );
    return Number.isFinite(contagem?.log10) ? contagem.log10 : null;
};

/**
 * A diluição do experimento entra na contagem? Não quando o crm-back gravou que a
 * análise vai pura ao tubo (páginas de coleta sem diluição). `undefined`/`true` =
 * resultado antigo ou análise com diluição.
 */
export const diluicaoSeAplica = (predicao) => predicao?.diluicaoAplicavel !== false;

/** Soma a diluição (expoente 10⁻ⁿ) ao log10 do modelo, se ela se aplica ao resultado. */
export const comDiluicao = (log10, diluicao, predicao) => {
    if (!Number.isFinite(log10)) return null;
    if (predicao && !diluicaoSeAplica(predicao)) return log10;
    const n = Number(diluicao);
    return log10 + (Number.isFinite(n) ? n : 0);
};

const normalizarNome = (texto) =>
    String(texto || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

/**
 * Nome da bactéria (como está no cadastro de Bactérias do IoT) → alvo do modelo.
 * ÚNICA tabela do sistema: bactéria nova com modelo novo = uma linha aqui.
 */
const ALVO_POR_NOME = Object.freeze({
    coliformestotais: ALVO.COLIFORMES_TOTAIS,
    coliformes: ALVO.COLIFORMES_TOTAIS,
    colitotais: ALVO.COLIFORMES_TOTAIS,
    colif: ALVO.COLIFORMES_TOTAIS,
    escherichiacoli: ALVO.ECOLI,
    ecoli: ALVO.ECOLI,
    mesofilostotais: ALVO.MESOFILOS_TOTAIS,
    mesofilos: ALVO.MESOFILOS_TOTAIS,
    aerobiosmesofilos: ALVO.MESOFILOS_TOTAIS,
    contagembacterianatotal: ALVO.MESOFILOS_TOTAIS,
    cbt: ALVO.MESOFILOS_TOTAIS,
});

export const alvoDaBacteria = (nome) => ALVO_POR_NOME[normalizarNome(nome)] ?? null;

const ROTULO_ALVO = Object.freeze({
    [ALVO.COLIFORMES_TOTAIS]: 'Coliformes totais',
    [ALVO.ECOLI]: 'Escherichia coli',
    [ALVO.MESOFILOS_TOTAIS]: 'Mesófilos totais',
});

export const rotuloDoAlvo = (alvo) => ROTULO_ALVO[alvo] || alvo;

/** Alvos regidos pela Portaria GM/MS 888 (água): só eles têm "conforme" em 100 mL. */
export const alvoDeAgua = (alvo) => alvo === ALVO.COLIFORMES_TOTAIS || alvo === ALVO.ECOLI;

/** "UFC/mL" → "UFC", "NMP/100mL" → "NMP"; sem unidade (registro antigo) = NMP. */
export const unidadeBase = (unit) => {
    const u = String(unit || '').trim().toUpperCase();
    if (!u) return 'NMP';
    if (u.startsWith('UFC')) return 'UFC';
    if (u.startsWith('NMP')) return 'NMP';
    return u;
};

// Posição histórica dos protocolos antigos: [0] coliformes, [1] E. coli.
const ALVO_POR_POSICAO = [ALVO.COLIFORMES_TOTAIS, ALVO.ECOLI];

/**
 * Linhas de resultado de um experimento: uma por bactéria do protocolo, casada com o
 * alvo do modelo pelo NOME (não pela posição), mais uma linha para cada alvo que o
 * modelo devolveu e nenhuma bactéria cobre — nenhum resultado fica escondido.
 *
 * Unidade da linha: NMP quando o modelo dá NMP (o modelo de coliformes/E. coli copia o
 * mesmo número em _nmp e _ufc e foi treinado contra laboratório em NMP — só o NMP é
 * comparável); senão a unidade do laboratório que o modelo tiver; senão a do modelo.
 * O laboratório em outra unidade continua aparecendo, com `labComparavel: false`.
 *
 * @param bacterias  [{ bacteriaId, bacteriaNome }] na ordem do protocolo
 * @param labResult  resultado de laboratório como vem da API (novo ou legado)
 * @returns [{ chave, alvo, titulo, bacteriaId, presenca, unidade, predLog,
 *             labValor, labUnidade, labComparavel }]
 */
export function secoesDoResultado({ predicao, bacterias = [], labResult, diluicao = 0 }) {
    const lab = normalizarResultadoLab(labResult)?.bacterias || [];
    const alvosDoModelo = (predicao?.alvos || []).map((a) => a?.alvo).filter(Boolean);
    const usados = new Set();

    const labDa = (bacteriaId, alvo, posicao) => {
        const porId = bacteriaId ? lab.filter((x) => x?.bacteriaId === bacteriaId) : [];
        if (porId.length) return porId;
        const porNome = alvo ? lab.filter((x) => !x?.bacteriaId && alvoDaBacteria(x?.bacteriaNome) === alvo) : [];
        if (porNome.length) return porNome;
        const legado = posicao != null ? lab[posicao] : null;
        return legado && !legado.bacteriaId ? [legado] : [];
    };

    const montar = (alvo, titulo, bacteriaId, posicao) => {
        const labs = labDa(bacteriaId, alvo, posicao);
        const unidadesModelo = (alvoDa(predicao, alvo)?.contagens || [])
            .filter((c) => Number.isFinite(c?.log10))
            .map((c) => c.unidade);
        const unidadesLab = labs.map((x) => unidadeBase(x?.unit));
        const unidade =
            (unidadesModelo.includes('NMP') ? 'NMP' : null) ||
            unidadesLab.find((u) => unidadesModelo.includes(u)) ||
            unidadesModelo[0] ||
            unidadesLab[0] ||
            // Alvo ausente (sem contagem) de água: a unidade histórica da tela.
            (alvoDeAgua(alvo) ? 'NMP' : null);
        const mesmaUnidade = labs.find((x) => unidadeBase(x?.unit) === unidade) || null;
        const entradaLab = mesmaUnidade || labs[0] || null;
        const presenca = alvo ? presencaDoAlvo(predicao, alvo) : undefined;
        const predLog =
            !predicao || !alvo || presenca === false || isSoPresenca(predicao) || !unidade
                ? null
                : comDiluicao(contagemLog10(predicao, alvo, unidade), diluicao, predicao);
        return {
            chave: bacteriaId || alvo || `posicao-${posicao}`,
            alvo,
            titulo,
            bacteriaId: bacteriaId || null,
            presenca,
            unidade,
            predLog,
            labValor: entradaLab?.nmp ?? null,
            labUnidade: entradaLab ? entradaLab.unit || null : null,
            labComparavel: Boolean(mesmaUnidade),
        };
    };

    const secoes = bacterias.map((b, i) => {
        let alvo = alvoDaBacteria(b?.bacteriaNome);
        if (!alvo && alvosDoModelo.includes(ALVO_POR_POSICAO[i])) alvo = ALVO_POR_POSICAO[i];
        if (alvo) usados.add(alvo);
        return montar(alvo, b?.bacteriaNome || rotuloDoAlvo(alvo), b?.bacteriaId, i);
    });
    alvosDoModelo.forEach((alvo) => {
        if (usados.has(alvo)) return;
        usados.add(alvo);
        secoes.push(montar(alvo, rotuloDoAlvo(alvo), null, null));
    });
    return secoes;
}
